import { MissionBrand } from "@/components/mission-chrome";
import {
  intersectionObserverRoot,
  maxScrollTop,
  nearestScrollRoot,
  scrolledFromOffset,
} from "@/lib/scroll-root";
import { cn } from "@/lib/utils";
import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import { Link } from "react-router-dom";

const linkClass =
  "hidden text-[12px] tracking-[0.08em] text-white/72 uppercase no-underline transition-colors hover:text-white sm:inline";

export function LandingNav({ scrollerRef }: { scrollerRef: RefObject<HTMLDivElement | null> }) {
  const [scrolled, setScrolled] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const prefer = scrollerRef.current;
    const sentinel = sentinelRef.current;
    const scrollingElement = document.scrollingElement;
    const fallback = (scrollingElement ?? document.documentElement) as HTMLElement | null;
    const getStyle = (el: HTMLElement) => {
      const s = getComputedStyle(el);
      return { overflowY: s.overflowY, overflow: s.overflow };
    };
    const root = nearestScrollRoot<HTMLElement>(sentinel ?? prefer, prefer, getStyle, fallback);
    const ioRoot = intersectionObserverRoot(root, [document.documentElement, document.body, scrollingElement]);

    let cancelled = false;
    let innerGone = false;
    let viewportGone = false;
    const readScrolled = () =>
      scrolledFromOffset(
        maxScrollTop([prefer?.scrollTop ?? 0, root?.scrollTop ?? 0, fallback?.scrollTop ?? 0, window.scrollY]),
      );
    const apply = () => {
      if (cancelled) return;
      setScrolled(innerGone || viewportGone || readScrolled());
    };
    apply();

    const targets = new Set<EventTarget>();
    if (prefer) targets.add(prefer);
    if (root) targets.add(root);
    targets.add(window);
    for (const target of targets) target.addEventListener("scroll", apply, { passive: true });

    const observers: IntersectionObserver[] = [];
    if (sentinel && typeof IntersectionObserver !== "undefined") {
      const watch = (rootEl: Element | null, assign: (gone: boolean) => void) => {
        const io = new IntersectionObserver(
          ([entry]) => {
            if (cancelled || !entry) return;
            assign(!entry.isIntersecting);
            apply();
          },
          { root: rootEl, threshold: 0 },
        );
        io.observe(sentinel);
        observers.push(io);
      };
      watch(null, (gone) => {
        viewportGone = gone;
      });
      const innerRoot =
        prefer && prefer !== document.documentElement && prefer !== document.body ? prefer : ioRoot;
      if (innerRoot) {
        watch(innerRoot, (gone) => {
          innerGone = gone;
        });
      }
    }

    return () => {
      cancelled = true;
      for (const target of targets) target.removeEventListener("scroll", apply);
      for (const io of observers) io.disconnect();
    };
  }, [scrollerRef]);

  return (
    <>
      <nav
        data-scrolled={scrolled ? "true" : "false"}
        className={cn(
          "sticky top-0 z-20 flex h-[72px] items-center gap-3 border-b px-4 backdrop-blur-md transition-[background-color,border-color] duration-200 sm:gap-6 sm:px-10",
          scrolled ? "border-white/10 bg-black/80" : "border-white/8 bg-black/40",
        )}
      >
        <MissionBrand />
        <div className="flex-1" />
        {/* Private repo + no public docs site. Do not link a GitHub blob or a fake /docs route. */}
        <span
          className="hidden cursor-default text-[12px] tracking-[0.08em] text-white/45 uppercase sm:inline"
          title="Docs coming"
        >
          Docs · coming
        </span>
        <a href="#stages" className={linkClass}>
          Mission stages
        </a>
        <a href="#how" className={linkClass}>
          How
        </a>
        <Link
          to="/privacy"
          rel="privacy-policy"
          className="text-[12px] tracking-[0.08em] text-white/72 uppercase no-underline transition-colors hover:text-white"
        >
          Privacy
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center rounded-full border border-white/28 px-3 py-2 text-[12px] font-semibold tracking-[0.06em] text-white uppercase no-underline sm:px-5 sm:py-2.5"
        >
          Sign in
        </Link>
      </nav>
      {/* Out of flow so hero -mt-[72px] still tucks under the 72px bar. */}
      <div className="relative h-0 w-full" aria-hidden="true">
        <div
          ref={sentinelRef}
          data-landing-nav-sentinel=""
          className="pointer-events-none absolute top-0 left-0 h-px w-full"
        />
      </div>
    </>
  );
}
